import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const pagedAttentionComponent: ComponentDef = {
  id: 'paged-attention',
  name: 'Paged Attention',
  description: 'vLLM-style paged attention: KV cache is stored in fixed-size physical blocks mapped by a page table.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'block_size', label: 'Block size', type: 'number', default: 16, min: 1, max: 256, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'paged_kv',
      options: [
        { label: 'Paged KV Management', value: 'paged_kv' },
        { label: 'Block Table & Copy-on-Write', value: 'cow' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const seq_len = Number(params.seq_len);
    const block_size = Number(params.block_size);
    const d_head = d_model / n_heads;
    const variant = String(params.variant);
    const n_blocks = Math.ceil(seq_len / block_size);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    g.linear('wq', 'x', [d_model, d_model]);
    g.linear('wk', 'x', [d_model, d_model]);
    g.linear('wv', 'x', [d_model, d_model]);

    g.reshape('q', 'wq', [n_heads, seq_len, d_head]);
    g.reshape('k', 'wk', [n_heads, seq_len, d_head]);
    g.reshape('v', 'wv', [n_heads, seq_len, d_head]);

    if (variant === 'cow') {
      // Block Table & Copy-on-Write for beam search / parallel sampling
      g.router('block_table', 'q', [n_blocks, block_size]);
      g.scale('prefix_check', 'block_table', 'Shared Prefix?');
      g.split('shared_block', 'prefix_check', [n_blocks, block_size], 'shared');
      g.split('unique_block', 'prefix_check', [n_blocks, block_size], 'unique');
      // Copy-on-write: duplicate shared block into per-sequence private block
      g.scale('cow_dup', 'shared_block', 'Copy-on-Write');
      g.cache('kv_private', 'cow_dup', [n_heads, n_blocks * block_size, d_head]);
      // Append new KV to per-sequence blocks
      g.concat('kv_blocks', ['kv_private', 'unique_block'], [n_heads, seq_len, d_head]);
    } else {
      // Paged KV Management (default): page table maps logical to physical blocks
      g.router('page_table', 'q', [seq_len, 1]);
      g.cache('kv_blocks', 'k', [n_heads, seq_len, d_head]);
    }

    g.transpose('kt', 'kv_blocks', [n_heads, d_head, seq_len], 'K^T');
    g.matmul('qk', 'q', 'kt', [n_heads, seq_len, seq_len]);
    g.softmax('softmax', 'qk', -1);
    g.matmul('attn_v', 'softmax', 'kv_blocks', [n_heads, seq_len, d_head]);
    g.reshape('out', 'attn_v', [seq_len, d_model]);
    g.output('out', 'out');
    return g.build();
  }
};
